import { useMemo, useState } from 'react';
import Reveal from '../components/Reveal';
import { useLanguage } from '../context/LanguageContext';
import { getYouTubeEmbedUrl } from '../utils/getYouTubeEmbedUrl';
import investmentVideo from '../videos/services/investment.mp4';

const images = {
  hero:
    'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=1800&q=80',
  narrative: [
    'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1400&q=80',
    'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1400&q=80',
    'https://images.unsplash.com/photo-1507670092296-5c5c6a2b5cc3?auto=format&fit=crop&w=1400&q=80',
    'https://plus.unsplash.com/premium_photo-1695185954879-73b06af2f35d?q=80&w=687&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    'https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?auto=format&fit=crop&w=1400&q=80',
    'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1400&q=80',
    'https://images.unsplash.com/photo-1556761175-b413da4baf72?q=80&w=1074&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  ],
  revenue: [
    'https://cdn.wallpapersafari.com/54/46/ZRPcyu.jpeg',
    'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=900&q=80',
    'https://assets.bwwsplatform.com/targetauction/assets/content/blog/1540/green_auction_button__gavel__primary.jpg?v=1685119875',
    'https://wp.gingermediagroup.com/wp-content/uploads/2023/10/tv-advertising-1.jpg',
    'https://exposureanalytics.com/wp-content/uploads/2025/01/WhatsApp-Image-2024-11-12-at-12.11.31-845x400.jpeg',
  ],
  banners: {
    model: 'https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&w=1800&q=80',
    financial: 'https://images.unsplash.com/photo-1642790106117-e829e14a795f?auto=format&fit=crop&w=1800&q=80',
    scenarios: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1800&q=80',
    terms: 'https://images.unsplash.com/photo-1553729459-efe14ef6055d?auto=format&fit=crop&w=1800&q=80',
  },
  investment: "https://www.deskera.com/blog/content/images/2022/07/Punjab--30-.png",
  expansion:
    'https://plus.unsplash.com/premium_photo-1755001218706-9738406ccc55?q=80&w=715&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  membership:
    'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=900&q=80',
  geo:
    'https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&w=900&q=80',
  funding:
    'https://blog.fortunaadvisors.com.au/hubfs/business%20funding.jpg',
  milestones: [
    'https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=900',
    'https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=900',
    'https://images.pexels.com/photos/7688336/pexels-photo-7688336.jpeg?auto=compress&cs=tinysrgb&w=900',
  ],
  exit: 'https://cdn.corporatefinanceinstitute.com/assets/investment-1024x686.jpeg',
  governance:
    'https://img.magnific.com/premium-photo/investing-stock-market-concept-businessman-checks-financial-data-charts-trading-forex-stocks-money-digital-assets-business-finance-technology-investment_144352-1138.jpg?semt=ais_hybrid&w=740&q=80',
  conclusion:
    'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1800&q=80',
};

function FeatureImage({ src, alt, className = '' }) {
  return (
    <div
      className={`investment-feature-image ${className}`.trim()}
      style={{ backgroundImage: `url(${src})` }}
      role="img"
      aria-label={alt}
    />
  );
}

function SectionBanner({ src, alt, title, subtitle }) {
  return (
    <Reveal>
      <div className="investment-section-banner">
        <FeatureImage src={src} alt={alt} />
        <div className="investment-section-banner-overlay" />
        <div className="investment-section-banner-text">
          {title ? <h2>{title}</h2> : null}
          {subtitle ? <p>{subtitle}</p> : null}
        </div>
      </div>
    </Reveal>
  );
}

function DataTable({ caption, headers, rows, footer, compact }) {
  return (
    <div className={`investment-table-wrap${compact ? ' investment-table-wrap-compact' : ''}`}>
      <table className="investment-table">
        {caption ? <caption>{caption}</caption> : null}
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header} scope="col">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.join('|')}>
              {row.map((cell, index) => (
                <td key={`${row[0]}-${index}`}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
        {footer ? (
          <tfoot>
            <tr>
              {footer.map((cell, index) => (
                <td key={`footer-${index}`}>{cell}</td>
              ))}
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}

function NarrativeContent({ section }) {
  return (
    <article className="investment-narrative-card">
      <div className="investment-narrative-head">
        <span className="material-symbols-outlined investment-narrative-icon">{section.icon}</span>
        <h3>{section.title}</h3>
      </div>
      {section.paragraphs?.map((para) => (
        <p key={para}>{para}</p>
      ))}
      {section.bullets?.length > 0 ? (
        <ul className="bullets investment-bullets">
          {section.bullets.map((item) => (
            <li key={item}>
              <span className="material-symbols-outlined check">check_circle</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {section.closingParagraph ? (
        <p className="investment-closing">{section.closingParagraph}</p>
      ) : null}
    </article>
  );
}

function NarrativeRow({ section, index, imageSrc }) {
  const reverse = index % 2 === 1;

  return (
    <article
      className={`investment-feature-row${reverse ? ' investment-feature-row-reverse' : ''}`}
    >
      <Reveal direction={reverse ? 'right' : 'left'}>
        <NarrativeContent section={section} />
      </Reveal>
      <Reveal direction={reverse ? 'left' : 'right'}>
        <FeatureImage src={imageSrc} alt={section.title} />
      </Reveal>
    </article>
  );
}

function InvestmentPage() {
  const { t } = useLanguage();
  const page = t.investmentPage;
  const [fullProposalOpen, setFullProposalOpen] = useState(false);
  const narrativeSections = useMemo(() => page.sections ?? [], [page.sections]);
  const videoEmbedUrl = useMemo(
    () => getYouTubeEmbedUrl(page.video?.videoUrl),
    [page.video?.videoUrl]
  );
  const fileVideoSrc = page.video?.videoSrc || investmentVideo;

  return (
    <main className="inner-page investment-page">
      <section className="investment-hero-section">
        <div className="container">
          <Reveal>
            <div className="investment-hero-card">
              <div
                className="investment-hero-bg"
                style={{ backgroundImage: `url(${images.hero})` }}
                aria-hidden="true"
              />
              <span className="eyebrow investment-eyebrow">{page.eyebrow}</span>
              <h1>{page.title}</h1>
              <p>{page.subtitle}</p>
            </div>
          </Reveal>

          <div className="investment-hero-stats">
            {page.heroStats.map((stat, index) => (
              <Reveal key={stat.label} delay={80 + index * 70}>
                <article className="investment-stat-card">
                  <span className="investment-stat-value">{stat.value}</span>
                  <span className="investment-stat-label">{stat.label}</span>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal delay={120}>
            <div className="investment-hero-video-wrap">
              {page.video?.title ? (
                <h2 className="investment-hero-video-title">{page.video.title}</h2>
              ) : null}
              <div className="investment-hero-video-frame">
                {videoEmbedUrl ? (
                  <iframe
                    title={page.video.title}
                    src={`${videoEmbedUrl}?rel=0`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="strict-origin-when-cross-origin"
                  />
                ) : fileVideoSrc ? (
                  <video controls playsInline preload="metadata" src={fileVideoSrc} />
                ) : (
                  <div
                    className="investment-video-placeholder"
                    style={{ backgroundImage: `url(${images.hero})` }}
                  >
                    <div className="investment-video-placeholder-overlay" />
                    <div className="investment-video-placeholder-body">
                      <span className="material-symbols-outlined investment-video-placeholder-icon">
                        play_circle
                      </span>
                      <span className="investment-video-placeholder-label">
                        {page.video?.placeholder}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section investment-overview">
        <div className="container">
          <Reveal>
            <span className="eyebrow center">{page.overviewEyebrow}</span>
            <h2 className="center-title">{page.overviewTitle}</h2>
          </Reveal>
          <div className="investment-feature-list">
            {narrativeSections.map((section, index) => (
              <NarrativeRow
                key={section.title}
                section={section}
                index={index}
                imageSrc={images.narrative[index % images.narrative.length]}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="section investment-model">
        <div className="container">
          <SectionBanner
            src={images.banners.model}
            alt={page.modelTitle}
            title={page.modelTitle}
            subtitle={page.modelLead}
          />

          <div className="investment-model-grid">
            <Reveal>
              <article className="investment-panel investment-panel-media">
                <FeatureImage src={images.membership} alt={page.membership.title} className="investment-panel-image" />
                <div className="investment-panel-body">
                  <h3>{page.membership.title}</h3>
                  <DataTable
                    headers={page.membership.headers}
                    rows={page.membership.rows}
                    footer={page.membership.footer}
                  />
                </div>
                <FeatureImage
                  src={images.expansion}
                  alt={page.membership.title}
                  className="investment-panel-image investment-panel-image-bottom"
                />
              </article>
            </Reveal>

            <Reveal delay={80}>
              <article className="investment-panel investment-panel-media">
                <FeatureImage src={images.geo} alt={page.geoDistribution.title} className="investment-panel-image" />
                <div className="investment-panel-body">
                  <h3>{page.geoDistribution.title}</h3>
                  <DataTable
                    headers={page.geoDistribution.headers}
                    rows={page.geoDistribution.rows}
                    footer={page.geoDistribution.footer}
                    compact
                  />
                </div>
              </article>
            </Reveal>
          </div>

          <Reveal delay={100}>
            <article className="investment-panel investment-panel-wide investment-split-panel">
              <div className="investment-split-content">
                <h3>{page.expansion.title}</h3>
                <DataTable headers={page.expansion.headers} rows={page.expansion.rows} compact />
                <p className="investment-countries-note">{page.expansion.countriesNote}</p>
              </div>
              <FeatureImage src={images.investment} alt={page.expansion.title} className="investment-split-image" />
            </article>
          </Reveal>
        </div>
      </section>

      {!fullProposalOpen ? (
        <section className="section investment-financial">
          <div className="container">
            <Reveal>
              <div className="investment-gate">
                <h2 className="investment-gate-title">{page.gate.title}</h2>
                <p className="investment-gate-text">{page.gate.text}</p>
                <button
                  type="button"
                  className="primary-btn investment-gate-btn"
                  onClick={() => setFullProposalOpen(true)}
                >
                  <span className="material-symbols-outlined">expand_more</span>
                  {page.gate.button}
                </button>
              </div>
            </Reveal>
          </div>
        </section>
      ) : (
        <FullProposalSections page={page} />
      )}
    </main>
  );
}

function FullProposalSections({ page }) {
  return (
    <>
      <section className="section investment-financial">
        <div className="container">
          <SectionBanner
            src={images.banners.financial}
            alt={page.financialTitle}
            title={page.financialTitle}
            subtitle={page.revenueTotal.label}
          />

          <Reveal delay={60}>
            <div className="investment-revenue-total">
              <span className="investment-revenue-total-label">{page.revenueTotal.label}</span>
              <span className="investment-revenue-total-value">{page.revenueTotal.value}</span>
            </div>
          </Reveal>

          <div className="investment-revenue-grid">
            {page.revenueStreams.map((stream, index) => (
              <Reveal key={stream.title} delay={index * 70}>
                <article className="investment-revenue-card">
                  <FeatureImage
                    src={images.revenue[index % images.revenue.length]}
                    alt={stream.title}
                    className="investment-revenue-image"
                  />
                  <div className="investment-revenue-body">
                    <h3>{stream.title}</h3>
                    <ul className="investment-revenue-list">
                      {stream.items.map((item) => (
                        <li key={item.name}>
                          <span>{item.name}</span>
                          <strong>{item.amount}</strong>
                        </li>
                      ))}
                    </ul>
                    <p className="investment-revenue-subtotal">
                      <span>{stream.totalLabel}</span>
                      <strong>{stream.total}</strong>
                    </p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal delay={80}>
            <DataTable
              caption={page.revenueSummary.title}
              headers={page.revenueSummary.headers}
              rows={page.revenueSummary.rows}
              footer={page.revenueSummary.footer}
            />
          </Reveal>
        </div>
      </section>

      <section className="section investment-scenarios">
        <div className="container">
          <SectionBanner
            src={images.banners.scenarios}
            alt={page.scenarios.title}
            title={page.scenarios.title}
          />
          <div className="investment-scenario-grid">
            <Reveal>
              <article className="investment-scenario-card investment-scenario-target">
                <h3>{page.scenarios.target.title}</h3>
                <p>{page.scenarios.target.intro}</p>
                <ul className="bullets investment-bullets">
                  {page.scenarios.target.bullets.map((item) => (
                    <li key={item}>
                      <span className="material-symbols-outlined check">check_circle</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>
            </Reveal>
            <Reveal delay={90}>
              <article className="investment-scenario-card">
                <h3>{page.scenarios.conservative.title}</h3>
                <p>{page.scenarios.conservative.intro}</p>
                <p className="investment-highlight">{page.scenarios.conservative.highlight}</p>
                {page.scenarios.conservative.noteTitle ? (
                  <h4 className="investment-scenario-note-title">
                    {page.scenarios.conservative.noteTitle}
                  </h4>
                ) : null}
                <p>{page.scenarios.conservative.note}</p>
              </article>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="section investment-terms">
        <div className="container">
          <SectionBanner
            src={images.banners.terms}
            alt={page.termsTitle}
            title={page.termsTitle}
            subtitle={page.termsEyebrow}
          />

          <div className="investment-terms-grid">
            <Reveal>
              <article className="investment-panel investment-panel-media">
                <FeatureImage src={images.exit} alt={page.ownership.title} className="investment-panel-image" />
                <div className="investment-panel-body">
                  <h3>{page.ownership.title}</h3>
                  <DataTable headers={page.ownership.headers} rows={page.ownership.rows} compact />
                  <p className="investment-note">{page.ownership.note}</p>
                </div>
              </article>
            </Reveal>

            <Reveal delay={70}>
              <article className="investment-panel investment-panel-media">
                <FeatureImage src={images.governance} alt={page.profitPolicy.title} className="investment-panel-image" />
                <div className="investment-panel-body">
                  <h3>{page.profitPolicy.title}</h3>
                  <ul className="investment-policy-list">
                    {page.profitPolicy.items.map((item) => (
                      <li key={item.label}>
                        <span className="investment-policy-share">{item.share}</span>
                        <span>{item.label}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="investment-note">{page.profitPolicy.note}</p>
                </div>
              </article>
            </Reveal>
          </div>

          <Reveal delay={90}>
            <article className="investment-panel investment-panel-wide investment-funding-panel investment-split-panel">
              <div className="investment-split-content">
                <h3>{page.funding.title}</h3>
                <p className="investment-funding-lead">{page.funding.lead}</p>
                <p>{page.funding.body}</p>
                <div className="investment-tranche-grid">
                  {page.funding.tranches.map((tranche) => (
                    <div key={tranche.label} className="investment-tranche-card">
                      <strong>{tranche.amount}</strong>
                      <span>{tranche.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <FeatureImage src={images.funding} alt={page.funding.title} className="investment-split-image" />
            </article>
          </Reveal>

          <Reveal delay={100}>
            <article className="investment-panel investment-panel-wide">
              <h3>{page.capitalUse.title}</h3>
              <DataTable
                headers={page.capitalUse.headers}
                rows={page.capitalUse.rows}
                footer={page.capitalUse.footer}
              />
            </article>
          </Reveal>

          <div className="investment-milestones-grid">
            {page.milestones.map((phase, index) => (
              <Reveal key={phase.title} delay={index * 80}>
                <article className="investment-milestone-card">
                  <FeatureImage
                    src={images.milestones[index % images.milestones.length]}
                    alt={phase.title}
                    className="investment-milestone-image"
                  />
                  <div className="investment-milestone-body">
                    <h3>{phase.title}</h3>
                    <ul className="bullets investment-bullets">
                      {phase.items.map((item) => (
                        <li key={item}>
                          <span className="material-symbols-outlined check">check_circle</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section investment-closing-section">
        <div className="container">
          <div className="investment-closing-grid">
            <Reveal>
              <article className="investment-panel investment-panel-media">
                <FeatureImage src={images.exit} alt={page.exitPlan.title} className="investment-panel-image" />
                <div className="investment-panel-body">
                  <h3>{page.exitPlan.title}</h3>
                  <p>{page.exitPlan.text}</p>
                </div>
              </article>
            </Reveal>
            <Reveal delay={70}>
              <article className="investment-panel investment-panel-media">
                <FeatureImage src={images.governance} alt={page.governance.title} className="investment-panel-image" />
                <div className="investment-panel-body">
                  <h3>{page.governance.title}</h3>
                  <p>{page.governance.text}</p>
                </div>
              </article>
            </Reveal>
          </div>

          <Reveal delay={100}>
            <article className="investment-conclusion">
              <FeatureImage
                src={images.conclusion}
                alt={page.conclusion.title}
                className="investment-conclusion-bg"
              />
              <div className="investment-conclusion-overlay" />
              <div className="investment-conclusion-content">
                <h3>{page.conclusion.title}</h3>
                {page.conclusion.paragraphs.map((para) => (
                  <p key={para}>{para}</p>
                ))}
              </div>
            </article>
          </Reveal>
        </div>
      </section>
    </>
  );
}

export default InvestmentPage;
